import { useCallback, useState } from "react";
import { useAuth } from "../context/ApiKeyContext";
import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();

/**
 * Hook customizado para gerenciar companies e tenants
 * Permite buscar e gerenciar dados de empresas e inquilinos
 */
export const useUserManagement = () => {
  const { token } = useAuth();

  // Estados para companies
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Estados para tenants
  const [tenantOptions, setTenantOptions] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  /**
   * Busca companies com filtro opcional
   * @param {string} searchQuery - Query de busca opcional
   * @returns {Promise<Array>} Lista de companies
   */
  const searchCompanies = useCallback(
    async (searchQuery = "") => {
      if (!token) {
        console.error("Token de autenticação não encontrado");
        return [];
      }

      setLoadingCompanies(true);
      try {
        const url = searchQuery
          ? `${API_BASE_URL}/users/companies?company_name=${encodeURIComponent(
              searchQuery
            )}`
          : `${API_BASE_URL}/users/companies`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const companies = data.map((company) => ({
            id: company.company_id,
            name: company.company_name,
            tenants: company.tenants || [],
          }));
          setCompanyOptions(companies);
          return companies;
        } else {
          console.error("Erro ao buscar companies:", response.status);
          return [];
        }
      } catch (error) {
        console.error("Erro ao buscar companies:", error);
        return [];
      } finally {
        setLoadingCompanies(false);
      }
    },
    [token]
  );

  /**
   * Busca tenants baseado na company selecionada
   * @param {string} searchQuery - Query de busca opcional
   * @returns {Promise<Array>} Lista de tenants
   */
  const searchTenants = useCallback(
    async (searchQuery = "") => {
      if (!token) {
        console.error("Token de autenticação não encontrado");
        return [];
      }

      if (!selectedCompany) {
        console.warn("Nenhuma company selecionada para buscar tenants");
        return [];
      }

      setLoadingTenants(true);
      try {
        const url = searchQuery
          ? `${API_BASE_URL}/users/companies?company_name=${encodeURIComponent(
              selectedCompany.name
            )}&tenant_name=${encodeURIComponent(searchQuery)}`
          : `${API_BASE_URL}/users/companies?company_name=${encodeURIComponent(
              selectedCompany.name
            )}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const company = data.find((c) => c.company_id === selectedCompany.id);
          if (company && company.tenants) {
            const tenants = company.tenants.map((tenant) => ({
              id: tenant.tenant_id,
              name: tenant.tenant_name,
              cells: tenant.cells || [],
            }));
            setTenantOptions(tenants);
            return tenants;
          }
          return [];
        } else {
          console.error("Erro ao buscar tenants:", response.status);
          return [];
        }
      } catch (error) {
        console.error("Erro ao buscar tenants:", error);
        return [];
      } finally {
        setLoadingTenants(false);
      }
    },
    [token, selectedCompany]
  );

  /**
   * Define uma company selecionada e busca seus tenants
   * @param {Object} company - Company selecionada
   */
  const selectCompany = useCallback(
    async (company) => {
      setSelectedCompany(company);
      setSelectedTenant(null);
      if (company) {
        await searchTenants();
      } else {
        setTenantOptions([]);
      }
    },
    [searchTenants]
  );

  return {
    // Companies
    companyOptions,
    loadingCompanies,
    selectedCompany,
    searchCompanies,
    selectCompany,
    setSelectedCompany,

    // Tenants
    tenantOptions,
    loadingTenants,
    selectedTenant,
    setSelectedTenant,
    searchTenants,
    setTenantOptions,
  };
};

/**
 * Função utilitária para buscar companies sem usar o hook
 * Útil para componentes que não podem usar hooks
 */
export const fetchCompanies = async (token, searchQuery = "") => {
  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  try {
    const url = searchQuery
      ? `${API_BASE_URL}/users/companies?company_name=${encodeURIComponent(
          searchQuery
        )}`
      : `${API_BASE_URL}/users/companies`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.map((company) => ({
        id: company.company_id,
        name: company.company_name,
        tenants: company.tenants || [],
      }));
    } else {
      throw new Error(`Erro ao buscar companies: ${response.status}`);
    }
  } catch (error) {
    console.error("Erro ao buscar companies:", error);
    throw error;
  }
};

/**
 * Função utilitária para buscar tenants sem usar o hook
 * Útil para componentes que não podem usar hooks
 */
export const fetchTenants = async (token, companyName, searchQuery = "") => {
  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  if (!companyName) {
    throw new Error("Nome da company é obrigatório");
  }

  try {
    const url = searchQuery
      ? `${API_BASE_URL}/users/companies?company_name=${encodeURIComponent(
          companyName
        )}&tenant_name=${encodeURIComponent(searchQuery)}`
      : `${API_BASE_URL}/users/companies?company_name=${encodeURIComponent(
          companyName
        )}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      const company = data.find((c) => c.company_name === companyName);
      if (company && company.tenants) {
        return company.tenants.map((tenant) => ({
          id: tenant.tenant_id,
          name: tenant.tenant_name,
          cells: tenant.cells || [],
        }));
      }
      return [];
    } else {
      throw new Error(`Erro ao buscar tenants: ${response.status}`);
    }
  } catch (error) {
    console.error("Erro ao buscar tenants:", error);
    throw error;
  }
};
